import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, Tag, ArrowLeft } from "lucide-react";
import { client } from "../lib/config";
import SEOHead, { useSiteBase, articleSchema, breadcrumbSchema } from "../lib/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";

// Restrict markdown links to safe protocols
function SafeLink({ href, children, ...props }) {
  const safe = /^(https?:|mailto:|tel:|\/)/i.test(href || "");
  if (!safe) return <span>{children}</span>;
  const isExternal = /^https?:/i.test(href);
  return (
    <a href={href} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined} {...props}>
      {children}
    </a>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { base } = useSiteBase();

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    client.get(`/blog-posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="wb-container py-24 text-center">
        <h1 className="wb-h2">Post not found</h1>
        <button onClick={() => navigate("/blog")} className="wb-btn-outline mt-6">
          <ArrowLeft className="h-4 w-4" /> Back to Journal
        </button>
      </section>
    );
  }

  if (!post) {
    return <section className="wb-container py-24 text-center text-[#4A4641]">Loading…</section>;
  }

  return (
    <>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        image={post.hero}
        ogType="article"
        canonicalPath={`/blog/${post.slug}`}
        schema={[
          articleSchema(base, post),
          breadcrumbSchema(base, [
            { label: "Journal", to: "/blog" },
            { label: post.title, to: `/blog/${post.slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative bg-[#1A1A1A] text-white">
        <div className="absolute inset-0" aria-hidden="true">
          {post.hero && !post.hero.includes("/area-") ? (
            <>
              <img src={post.hero} alt="" className="w-full h-full object-cover opacity-45" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/80" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[#B94A15]/70 via-[#1A1A1A] to-[#1A1A1A]" />
              <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(55% 50% at 18% 25%, rgba(232,93,38,0.5) 0%, rgba(232,93,38,0) 60%), radial-gradient(45% 40% at 82% 78%, rgba(245,185,141,0.28) 0%, rgba(245,185,141,0) 60%)" }} />
            </>
          )}
        </div>
        <div className="relative wb-container py-20 md:py-28">
          <div className="wb-eyebrow text-[#FF9666]">{post.category}</div>
          <h1 className="wb-h1 text-white mt-4 max-w-3xl">{post.title}</h1>
          <p className="wb-body text-white/85 mt-5 max-w-2xl">{post.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-[#FF9666]" />
              {new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span>By {post.author}</span>
          </div>
        </div>
      </section>

      <Breadcrumbs items={[{ label: "Journal", to: "/blog" }, { label: post.title }]} />

      {/* Body */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <article className="lg:col-span-8 prose-content" data-testid="blog-post-content">
            <div className="wb-md">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{ a: SafeLink }}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t border-[#E5DFD3] flex flex-wrap items-center gap-2">
                <Tag className="h-4 w-4 text-[#B94A15]" />
                {post.tags.map((t) => (
                  <span key={t} className="px-2 py-1 text-xs bg-[#F5F0E6] text-[#4A4641] rounded-sm" data-testid={`blog-tag-${t.replace(/\s+/g, "-")}`}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </article>

          <aside className="lg:col-span-4 space-y-6">
            <div className="wb-card p-6 bg-white">
              <div className="wb-eyebrow mb-3">Related posts</div>
              <div className="space-y-3">
                {(post.related || []).map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="block group" data-testid={`related-post-${r.slug}`}>
                    <div className="text-sm font-bold text-[#1A1A1A] group-hover:text-[#B94A15]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                      {r.title}
                    </div>
                    <div className="text-xs text-[#4A4641] mt-1">{r.excerpt}</div>
                  </Link>
                ))}
                {(!post.related || post.related.length === 0) && (
                  <p className="text-sm text-[#4A4641]">More stories are on the way.</p>
                )}
              </div>
            </div>
            <div className="wb-card p-6 bg-[#1A1A1A] text-white">
              <div className="wb-eyebrow text-[#FF9666] mb-2">Stay with us</div>
              <p className="text-sm text-white/85">32 renovated rooms in Clinton, MO. Great service. Clean rooms.</p>
              <div className="mt-4 flex flex-col gap-2">
                <Link to="/rooms" className="wb-btn-primary text-sm py-2" data-testid="blog-sidebar-rooms">See rooms</Link>
                <Link to="/contact" className="wb-btn-outline text-sm py-2 border-[#FF9666] text-[#FF9666]" data-testid="blog-sidebar-contact">Contact</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <BookCallCTA />

      <style>{`
        .wb-md h2 { font-family: 'Nunito', sans-serif; font-weight: 900; font-size: 1.75rem; line-height: 1.2; color: #1A1A1A; margin: 2.5rem 0 1rem; }
        .wb-md h3 { font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 1.35rem; color: #1A1A1A; margin: 2rem 0 0.75rem; }
        .wb-md p { font-size: 1.05rem; line-height: 1.75; color: #2D2A26; margin: 1rem 0; }
        .wb-md ul, .wb-md ol { margin: 1rem 0; padding-left: 1.5rem; color: #2D2A26; }
        .wb-md li { margin: 0.35rem 0; line-height: 1.65; }
        .wb-md strong { color: #1A1A1A; font-weight: 700; }
        .wb-md a { color: #E85D26; text-decoration: underline; text-underline-offset: 2px; }
        .wb-md a:hover { color: #D04C1B; }
      `}</style>
    </>
  );
}
